// A10: reading and replying share one place. The complete lists in this box are what Send writes onto Graph.
import { Chip, ActionButton, ActionInput, ActionSelect, ActionTextarea } from '../parts/index.js';
import { useEffect, useRef, useState } from 'react';
import type { DraftRecipient, Option, PassItem } from '../api.js';
import { instructionOf, offerRead, replyDrafts, sendBoxReply, type BookAsk, type Booking, type OfferView, type ReplyDraftView, type ThreadMessageView } from './api.js';
import { bookedWords, bookLabel, clashWords, lengthsFor, offerLine } from './offer.js';
import './card-reply.css';

type People = { to: DraftRecipient[]; cc: DraftRecipient[] };
/** The card's pass item as the box reads it: its id, store and source. A Deals card passes its lead the same way. */
export type ReplyItem = Pick<PassItem, 'id' | 'store' | 'source'>;
const addressOf = (address: string) => address.trim().toLowerCase();
const nameOf = (p: DraftRecipient) => p.name || p.address;

export function boxRecipients(message: ThreadMessageView, self: string[], all: boolean): People {
  const seen = new Set(self.map(addressOf));
  const clean = (list: Array<{ name: string | null; address: string | null }>) => list.flatMap((p) => {
    if (!p.address) return [];
    const address = addressOf(p.address);
    if (seen.has(address)) return [];
    seen.add(address);
    return [{ name: p.name, address }];
  });
  const to = clean([{ name: message.from, address: message.fromAddress ?? null }, ...(all ? message.to : [])]);
  return { to, cc: all ? clean(message.cc) : [] };
}

export function canReplyAll(message: ThreadMessageView, self: string[]): boolean {
  const excluded = new Set([...self, message.fromAddress ?? ''].map(addressOf));
  return message.kind === 'email' && [...message.to, ...message.cc].some((p) => p.address && !excluded.has(addressOf(p.address)));
}

/** Apply only the differences to the existing recipient-edit route; no locally removed name is restored. */
async function keepPeople(id: string, store: string, before: People, after: People, kept: (view: ReplyDraftView) => void) {
  for (const recipientKind of ['to', 'cc'] as const) {
    for (const p of before[recipientKind]) if (!after[recipientKind].some((x) => x.address === p.address)) kept(await replyDrafts.recipients(id, store, recipientKind === 'to' ? { removeTo: p.address } : { removeCc: p.address }));
  }
  for (const recipientKind of ['to', 'cc'] as const) {
    for (const p of after[recipientKind]) if (!before[recipientKind].some((x) => x.address === p.address)) kept(await replyDrafts.recipients(id, store, recipientKind === 'to' ? { addTo: p.address } : { addCc: p.address }));
  }
}

export function ReplyBox({ item, message, self, all, option, onCancel, onSent, onBusy }: {
  item: ReplyItem; message: ThreadMessageView; self: string[]; all: boolean; option?: Option; onCancel: () => void;
  /** `after`: what the same press booked, or why it booked nothing (BUGS.md T69). */
  onSent: (names: string, after?: string) => void; onBusy: (busy: boolean) => void;
}) {
  const [people, setPeople] = useState<People>(() => boxRecipients(message, self, all));
  const [text, setText] = useState(option?.kind === 'reply' || option?.params?.voice === true ? '' : option?.label ?? '');
  const [draft, setDraft] = useState<ReplyDraftView | null>(null);
  const [writing, setWriting] = useState(!!option && option.kind !== 'reply');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adds, setAdds] = useState({ to: '', cc: '' });
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { box.current?.focus(); }, []);
  const email = item.source?.channel === 'email';
  // BUGS.md T69: a time the newest mail offers is booked on the same press, when he presses the button that says so.
  const [offer, setOffer] = useState<OfferView | null>(null);
  const [minutes, setMinutes] = useState(30);
  const signalId = item.source?.signalId;
  useEffect(() => {
    if (!email || !signalId || message.kind !== 'email') return;
    let live = true;
    offerRead(item.store, signalId).then((r) => {
      if (!live || !r.offer || r.offer.signalId !== message.id) return;
      setOffer(r.offer); setMinutes(r.offer.minutes);
    }).catch(() => { /* no offer read is no button: the plain Send is unchanged */ });
    return () => { live = false; };
  }, [email, signalId, item.store, message.id, message.kind]);
  const instruction = instructionOf(text);
  const write = !draft && (writing || instruction !== null);
  const go = async (bookIt = false) => {
    if (busy || !text.trim()) return;
    const book: BookAsk | undefined = bookIt && offer && !write ? { startsAt: offer.startsAt, minutes, hold: offer.late } : undefined;
    let booking: Booking | undefined;
    setBusy(true); onBusy(true); setError(null);
    try {
      if (write) {
        const purpose = option?.params?.purpose;
        const made = await replyDrafts.create({ store: item.store, signalId: item.source!.signalId, passItemId: item.id,
          purpose: purpose === 'chase' || option?.kind === 'chase' ? 'chase' : 'reply', instruction: instruction ?? text,
          all, ...(option ? { optionKey: option.key } : {}) });
        setDraft(made); setText(made.draft.bodyEn); setWriting(false);
        return;
      }
      let sentPeople = people;
      if (draft) {
        if (email) await keepPeople(draft.draft.id, item.store, draft.recipients ?? { to: [], cc: [] }, people, setDraft);
        const edited = text === draft.draft.bodyEn ? draft : await replyDrafts.edit(draft.draft.id, item.store, text);
        // Keep the locally accepted lists as the retry baseline if Send refuses after those edits reached the server.
        setDraft({ ...edited, recipients: people });
        const sent = await replyDrafts.send(draft.draft.id, item.store, 'en', option ? { passItemId: item.id, optionKey: option.key } : null, book);
        sentPeople = sent.recipients ?? people;
        booking = sent.booking;
      } else {
        const { sent, booking: made } = await sendBoxReply({ store: item.store, passItemId: item.id, signalId: item.source!.signalId, all, text, ...people, ...(book ? { book } : {}) });
        booking = made;
        if (email) sentPeople = { to: people.to.filter((p) => sent.to.includes(p.address)), cc: people.cc.filter((p) => sent.cc.includes(p.address)) };
      }
      const names = email ? [...sentPeople.to, ...sentPeople.cc].map(nameOf) : [message.from, ...message.to.filter((p) => !p.address || !self.includes(addressOf(p.address))).map((p) => p.name || p.address)].filter((n): n is string => !!n);
      const unique = [...new Set(names)];
      const after = offer ? bookedWords(booking, offer.zone) : null;
      onSent(unique.length > 1 ? `${unique.slice(0, -1).join(', ')} and ${unique.at(-1)}` : unique[0] ?? '', after ?? undefined);
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setBusy(false); onBusy(false); }
  };
  const add = (recipientKind: 'to' | 'cc') => {
    const address = addressOf(adds[recipientKind]);
    if (!/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(address) || self.map(addressOf).includes(address)) return;
    setPeople((p) => ({ to: p.to.filter((x) => x.address !== address), cc: p.cc.filter((x) => x.address !== address), [recipientKind]: [...p[recipientKind].filter((x) => x.address !== address), { name: [...message.to, ...message.cc].find((x) => x.address && addressOf(x.address) === address)?.name ?? null, address }] }));
    setAdds((p) => ({ ...p, [recipientKind]: '' }));
  };
  return <div className="state reply-box" data-state="typed" aria-label="Reply">
    {email ? (['to', 'cc'] as const).map((recipientKind) => <div key={recipientKind} className="reply-people">
      <span>{recipientKind === 'to' ? 'To' : 'Cc'}</span>{' '}
      {people[recipientKind].map((p) => <Chip key={p.address}  title={p.address}>{nameOf(p)} <ActionButton type="button" className="hx-link" aria-label={`remove ${nameOf(p)}`} disabled={busy} onClick={() => setPeople((v) => ({ ...v, [recipientKind]: v[recipientKind].filter((x) => x.address !== p.address) }))}>×</ActionButton></Chip>)}
      <ActionInput type="email" aria-label={`add ${recipientKind === 'to' ? 'To' : 'Cc'}`} value={adds[recipientKind]} disabled={busy} onChange={(e) => setAdds((p) => ({ ...p, [recipientKind]: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(recipientKind); } }} />
      <ActionButton type="button" className="hx-link" aria-label={`add to ${recipientKind === 'to' ? 'To' : 'Cc'}`} disabled={busy || !adds[recipientKind]} onClick={() => add(recipientKind)}>+</ActionButton>
    </div>) : null}
    <ActionTextarea className="draft" ref={box} aria-label="your reply" rows={4} value={text} disabled={busy} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); if (!busy) onCancel(); }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void go(); }
    }} />
    {offer && !write ? <div className="reply-offer">
      <p className="offer-line">{offerLine(offer)}</p>
      {clashWords(offer, minutes) ? <p className="offer-clash">{clashWords(offer, minutes)}</p> : null}
      <label>For <ActionSelect aria-label="how long" value={minutes} disabled={busy} onChange={(e) => setMinutes(Number(e.target.value))}>
        {lengthsFor(offer.minutes).map((m) => <option key={m} value={m}>{m} minutes</option>)}
      </ActionSelect></label>{' '}
      <ActionButton type="button" className="opt lead" disabled={busy || !text.trim() || (!offer.late && people.to.length + people.cc.length === 0)} onClick={() => void go(true)}>{bookLabel(offer, minutes, [...people.to, ...people.cc])}</ActionButton>
    </div> : null}
    <ActionButton type="button" className={offer && !write ? 'opt' : 'opt lead'} disabled={busy || !text.trim()} onClick={() => void go()}>{write ? 'Write it' : 'Send'}</ActionButton>
    <ActionButton reading type="button" className="hx-link" aria-label="Close" disabled={busy} onClick={onCancel}>×</ActionButton>
    {error ? <p className="error" role="alert">{error}</p> : null}
  </div>;
}
